<template>
  <div class="px-5 py-4">
    <div v-if="loading">
      <v-skeleton-loader type="card"></v-skeleton-loader>
    </div>
    <div v-else-if="product" class="flex flex-col">
      <div class="flex flex-col" style="gap:0.5rem;">
        <div class="plusGreyDarken4--text" style="font-size:1.438rem;">
          {{
            productId != "new"
              ? $t("message.products_memberships_plus.edit_product_or_membership")
              : $t("message.products_memberships_plus.add_product")
          }}</div>
        <div v-if="!product.type" class="plusGreyDarken2--text">
          {{ $t("message.products_memberships_plus.add_product_explained") }}</div>
      </div>
      <div style="padding-top:1.5rem">
        <v-select v-if="productId == 'new'" :items="types" outlined :label="$t('message.product_type')"
          v-model="productType" dense></v-select>
      </div>
      <div v-if="product.type" class="rounded" style="padding: 1rem; border:1px solid #E0E0E0;">
        <div class="flex flex-col" style="gap:1rem;">
          <div>Product details</div>
          <div class="flex flex-column flex-md-row" style="gap:2rem;">
            <div class="flex flex-col" style="flex:0 0 50%;gap:0.5rem;" v-if="product.type == 'good'">
              <div v-if="!productImage" class="flex flex-col plusGreyLighten5 justify-center items-center"
                style="gap:1.5rem;height:14.125rem;border:1px dashed #BDBDBD !important;">
                <div class="plusGreyDarken2--text">{{ $t("message.products_memberships_plus.upload_photo_product") }}
                </div>
                <PlusImageUploadCroppie @uploaded="successUploadImage"></PlusImageUploadCroppie>
              </div>
              <div v-if="!productImage" class="text-center plusGreyDarken1--text" style="font-size:0.875rem;">
                {{ $t("message.products_memberships_plus.upload_photo_recommended") }}</div>
              <div v-if="productImage" class="flex flex-col justify-center items-center plusIndigoLighten5 rounded"
                style="height:100%;gap:1rem;">
                <v-avatar size="250" rounded="0">
                  <ImageDisplay v-if="productImage" :src="productImage" />
                </v-avatar>
                <PlusImageUploadCroppie @uploaded="successUploadImage"
                  :label="$t('message.products_memberships_plus.change_image')"></PlusImageUploadCroppie>
              </div>
            </div>
            <div class="flex-grow-1 flex flex-col">
              <v-text-field class="flex-grow-0" outlined dense :label="$t('message.name')"
                v-model="product.name"></v-text-field>
              <ProductCategories v-if="product.type == 'good'" v-model="product.category"></ProductCategories>
              <v-textarea outlined :label="$t('message.description')" v-model="product.description"></v-textarea>
              <div class="flex" style="gap:1.5rem;">
                <v-text-field :disabled="this.productId != 'new' && product.type != 'good'" outlined dense
                  :label="$t('message.price')" v-mask="mask1" v-model="price"></v-text-field>
                <v-text-field :disabled="this.productId != 'new' && product.type != 'good'" outlined dense
                  v-mask="mask2" v-model="priceDecimal"></v-text-field>
                <v-text-field outlined dense disabled v-model="currencyText"
                  :label="$t('message.currency')"></v-text-field>
              </div>
            </div>
          </div>
        </div>
        <div class="pt-6" v-if="product.type == 'good' && !product.isMembership">
          <v-text-field class="pb-0 mb-0" :disabled="product.unlimited" outlined dense
            :label="$t('message.inventory_quantity') + ' (' + $t('message.optional') + ')'"
            v-model="product.availableQuantity"></v-text-field>
          <v-text-field v-if="product.type == 'good' && !product.isMembership" single-line outlined dense
            :label="$t('message.size') + ' (' + $t('message.optional') + ')'" v-model="product.size"></v-text-field>
          <v-switch color="plusIndigoDarken4" inset v-if="product.type == 'good' && !product.isMembership"
            v-model="showColorPicker" dense
            :label="$t('message.color') + ' (' + $t('message.optional') + ')'"></v-switch>
          <v-color-picker v-if="product.type == 'good' && showColorPicker && !product.isMembership"
            :hide-mode-switch="true" :hide-inputs="true" mode="hexa" v-model="product.color"></v-color-picker>
          <v-switch color="plusIndigoDarken4" inset class="mt-2 pt-0" dense v-model="product.unlimited"
            :label="$t('message.unlimited_quantity')"></v-switch>
          <v-switch color="plusIndigoDarken4" inset class="mt-0 pt-0" dense v-model="product.allowInTablet"
            :label="$t('message.allow_in_tablet')"></v-switch>
          <v-alert type="info" v-if="product.allowInTablet" dense text class="mt-1">
            {{ $t('message.allow_in_tablet_explained') }}
          </v-alert>
          <div class="text-caption" v-if="product.allowInTablet">{{ $t('message.allow_sold_tablet_explained') }}</div>
          <v-switch color="plusIndigoDarken4" inset class="mt-2 pt-0" dense
            v-if="product.type == 'good' && !product.isMembership && stripeReady"
            v-model="product.allowInConnect" :label="$t('message.allow_in_app')"></v-switch>
          <v-alert type="info" v-if="product.allowInConnect" dense text class="mt-1">
            {{ $t('message.allow_in_app_explained') }}
          </v-alert>
        </div>
        <div class="pt-6" v-if="product.type == 'good' && product.isMembership">
          <div class="gray--text text-caption mb-2">{{ $t('message.membership_days_explained') }}</div>
          <v-select :items="months" outlined
            :label="$t('message.membership_months') + ' (' + $t('message.optional').toLowerCase() + ')'"
            v-model="product.membershipMonths" dense clearable
            :disabled="product.membershipDays > 0 || product.membershipCheckins > 0 ? true : false"></v-select>
          <v-text-field type="number" outlined dense
            :label="$t('message.membership_days') + ' (' + $t('message.optional').toLowerCase() + ')'"
            :disabled="product.membershipMonths > 0 || product.membershipCheckins > 0 ? true : false"
            v-model="product.membershipDays">
          </v-text-field>
          <v-text-field type="number" outlined dense hide-details
            :label="$t('message.membership_checkins') + ' (' + $t('message.optional').toLowerCase() + ')'"
            :disabled="product.membershipDays > 0 || product.membershipMonths > 0 ? true : false"
            v-model="product.membershipCheckins">
          </v-text-field>
          <v-switch hide-details class="mt-4"
            v-if="product.membershipDays > 0 || product.membershipMonths > 0 || product.membershipCheckins > 0"
            v-model="product.manualRecurrence" color="plusIndigoDarken4" :label="$t('message.keep_membership_visible')">
          </v-switch>
          <div class="text-caption mt-1" v-if="product.manualRecurrence">
            {{ $t('message.keep_membership_visible_explained') }}
          </div>
        </div>
        <v-card v-if="product && product.isMembership && product._id" class="mb-2 mt-4" elevation="0" outlined>
          <v-card-text>
            <v-switch hide-details v-model="restrictAttendance" :label="$t('message.restrict_attendance')"></v-switch>
            <div class="text-caption">{{ $t('message.restrict_attendance_explained') }}</div>
          </v-card-text>
          <v-card-text v-if="restrictAttendance" class="mt-0 pt-0 mb-0 pb-0">
            <v-subheader>{{ $t('message.by_class_name') }}</v-subheader>
            <AvailableClasses v-model="product.allowedClasses"></AvailableClasses>
            <v-combobox class="mt-2" v-if="product.allowedCustomClasses" v-model="product.allowedCustomClasses"
              :items="product.allowedCustomClasses" small-chips outlined hide-details dense
              :label="$t('message.custom_names')" :multiple="true" clearable>
            </v-combobox>
            <v-subheader>{{ $t('message.by_class_count') }}</v-subheader>
            <div>
              <div class="mt-1">
                <v-select outlined dense :items="allowedClassIntervals" v-model="allowedClassCountInterval"
                  :label="$t('message.class_count_interval')" :clearable="true"></v-select>
              </div>
              <div v-if="allowedClassCountInterval">
                <v-text-field hide-details outlined dense v-model="allowedClassCount" number
                  :label="$t('message.allowed_classes')"></v-text-field>
                <v-switch hide-details v-model="allowedClassCountSendEmail"
                  :label="$t('message.send_email')"></v-switch>
                <div class="text-caption ml-1 gray--text">
                  {{ $t('message.allowed_class_count_send_email_explained') }}
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-text>
            <v-switch hide-details v-if="contracts.length > 0" v-model="assignContract"
              :label="$t('message.assign_contract')"></v-switch>
            <v-autocomplete hide-details v-show="assignContract" v-model="product.documentTemplate"
              :label="$t('message.contract')" :items="contracts" item-text="text" item-value="value" clearable
              outlined dense class="mt-2">
            </v-autocomplete>
            <span v-if="assignContract" class="text-caption gray--text">{{ $t('message.assign_contract_explained') }}</span>
          </v-card-text>
        </v-card>

        <!-- Inventory history -->
        <div
          v-if="product.type == 'good' && !product.unlimited && productId != 'new' && !product.isMembership"
          class="mt-4"
        >
          <div
            class="flex items-center justify-between px-4 cursor-pointer"
            style="border: 1px solid #E0E0E0; border-radius: 6px; min-height: 48px; background: #FAFAFA;"
            :style="showInventory ? 'border-bottom-left-radius:0;border-bottom-right-radius:0;' : ''"
            @click="toggleInventory()"
          >
            <div class="flex items-center" style="gap: 0.5rem;">
              <v-icon small color="plusIndigoDarken4">mdi-select-multiple</v-icon>
              <span class="plusGreyDarken4--text" style="font-size: 0.9375rem; font-weight: 500;">{{ $t("message.inventory") }}</span>
            </div>
            <v-icon>{{ showInventory ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
          <div v-if="showInventory" style="border: 1px solid #E0E0E0; border-top: none; border-radius: 0 0 6px 6px; padding: 0 0 4px;">
            <v-skeleton-loader type="list-item-avatar" v-if="loadingInventory"></v-skeleton-loader>
            <div v-else-if="inventory.length === 0" class="text-center plusGreyDarken1--text py-4" style="font-size: 0.875rem;">
              {{ $t("message.no_data") }}
            </div>
            <div v-else>
              <div
                v-for="(i, index) in inventory" :key="'inv_' + index"
                class="flex items-center py-3"
                style="gap: 0.75rem; border-bottom: 1px solid #F0F0F0; padding-left: 1rem;"
              >
                <v-avatar size="36" v-if="inventoryUserImage(i)">
                  <ImageDisplay :src="inventoryUserImage(i)" />
                </v-avatar>
                <v-avatar size="36" color="plusIndigoLighten3" v-else>
                  <span class="font-roboto white--text text-subtitle-1">{{
                    getInitials(i.user && i.user.displayName ? i.user.displayName
                      : i.transaction && i.transaction.buyer && i.transaction.buyer.displayName ? i.transaction.buyer.displayName
                      : i.email ? i.email : '')
                  }}</span>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="flex items-center" style="gap: 0.4rem;">
                    <v-icon small>{{ i.action == 'edit' ? 'mdi-pencil-box-outline' : 'mdi-cart-outline' }}</v-icon>
                    <span class="plusGreyDarken4--text" style="font-size: 0.875rem; font-weight: 500;">Total: {{ i.totalInventory }}</span>
                  </div>
                  <div class="plusGreyDarken1--text" style="font-size: 0.8125rem;">{{
                    i.user && i.user.displayName ? i.user.displayName
                      : i.transaction && i.transaction.buyer && i.transaction.buyer.displayName ? i.transaction.buyer.displayName
                      : i.email || ''
                  }}</div>
                  <div class="flex items-center" style="gap: 0.5rem; font-size: 0.8125rem;">
                    <span class="plusGreyDarken1--text">{{ $d(new Date(i.createdAt), "month_day_year", $i18n.locale) }}</span>
                    <span :class="i.amount > 0 ? 'success--text' : 'error--text'" style="font-weight: 600;">
                      {{ i.amount > 0 ? '+' + i.amount : i.amount }}
                    </span>
                  </div>
                </div>
                <v-btn v-if="i.transaction" icon small @click="goToTransaction(i.transaction)">
                  <v-icon small color="plusGreyDarken1">mdi-card-search-outline</v-icon>
                </v-btn>
              </div>
              <div v-if="inventoryHasMore" class="text-center py-3">
                <v-btn text small :loading="loadingMoreInventory" color="plusIndigoDarken4" @click="loadMoreInventory()">
                  {{ $t('message.view_more') }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <v-select v-if="productId == 'new' && product.type == 'service'" :items="intervals" outlined dense
          :label="$t('message.frequency')" v-model="product.interval"></v-select>

        <v-card class="pa-6 mb-2 mt-4" v-if="automaticTaxes" elevation="0" outlined>
          <div class="text-subtitle-2 gray--text">{{ $t('message.manage_taxes') }}</div>
          <div class="text-caption gray--text" v-if="product.taxBehavior">
            <span v-if="product.taxBehavior == taxBehaviors.exclusive">
              {{ $t('message.tax_behavior_excusive') }}
            </span>
            <span v-else-if="product.taxBehavior == taxBehaviors.inclusive">
              {{ $t('message.tax_behavior_inclusive') }}
            </span>
          </div>
          <v-radio-group v-model="product.taxBehavior" class="mb-0 pb-0">
            <v-radio :label="$t('message.none')" :value="taxBehaviors.none"></v-radio>
            <v-radio :label="$t('message.inclusive')" :value="taxBehaviors.inclusive"></v-radio>
            <v-radio :label="$t('message.exclusive')" :value="taxBehaviors.exclusive"></v-radio>
          </v-radio-group>
          <div v-if="product.taxBehavior">
            <v-autocomplete v-if="taxCodes" :items="taxCodes" v-model="product.taxCategory"
              :label="$t('message.tax_category')" item-text="name" item-value="id" outlined dense>
              <template v-slot:item="data">
                <v-list-item-content class="text-left">
                  <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ data.item.description }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>
        </v-card>
      </div>

      <div class="gray--text text-caption mt-2" v-if="product && product.type == 'service'">
        {{ $t('message.prices_explained') }}
      </div>

      <div v-if="product.type" class="flex pt-12" :class="{ 'flex-wrap': isSmallerDisplay() }" style="gap:1.25rem;">
        <div class="flex" style="gap:1.25rem;" :class="{ 'flex-wrap': isSmallerDisplay() }">
          <PlusButton large @click="goBack()">{{ $t('message.cancel') }}</PlusButton>
          <PlusButton :loading="updating" v-if="product._id" v-allowed="'edit_products'" intent="primary" large
            @click="addEditProduct()" :disabled="!currency ? true : false">
            {{ $t("message.products_memberships_plus.edit_product") }}</PlusButton>
          <PlusButton :loading="updating" v-else v-allowed="'add_products'" intent="primary" large
            @click="addEditProduct()" :disabled="!currency ? true : false">
            {{ $t("message.products_memberships_plus.add_product") }}</PlusButton>
          <PlusButton v-if="canDuplicate"
            v-allowed="'edit_products'" large @click="showDuplicateProductDialog()">
            {{ $t('message.duplicate') }}</PlusButton>
        </div>
        <div>
          <PlusButton :loading="updating" intent="error" v-if="product._id" large @click="showDeleteProductDialog()">
            {{ $t("message.delete") }}</PlusButton>
        </div>
      </div>
      <div v-if="!product.type" class="flex" style="gap:1.25rem;">
        <PlusButton large @click="goBack()">{{ $t("message.cancel") }}</PlusButton>
      </div>
    </div>
    <div v-else class="flex" style="gap:1.25rem;">
      <PlusButton large @click="goBack()">{{ $t("message.cancel") }}</PlusButton>
    </div>
    <v-dialog v-model="deleteProductDialog" content-class="fit-content">
      <v-card class="pa-2">
        <v-card-text>{{ $t('message.delete_product_confirm') }}</v-card-text>
        <v-card-actions>
          <PlusButton :loading="deletingProduct" intent="error" @click.stop="deleteProduct()">{{
            $t('message.delete') }}</PlusButton>
          <PlusButton elevation="0" @click.stop="deleteProductDialog = false">{{
            $t('message.close')
          }}</PlusButton>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="duplicateProductDialog" content-class="fit-content">
      <v-card class="pa-4" v-if="duplicateProductDialog">
        <v-card-title>{{ $t('message.duplicate') }}</v-card-title>
        <v-card-text>
          <v-text-field outlined dense :label="$t('message.name')" v-model="duplicateProduct.name"></v-text-field>
          <div class="flex" style="gap:1rem;">
            <v-text-field outlined dense :label="$t('message.price')" v-mask="mask1"
              v-model="duplicateProduct.price"></v-text-field>
            <v-text-field outlined dense v-mask="mask2" v-model="duplicateProduct.priceDecimal"></v-text-field>
            <v-text-field outlined dense disabled v-model="currencyText" :label="$t('message.currency')"></v-text-field>
          </div>
          <v-switch class="mt-0 pt-0" dense v-model="duplicateProduct.unlimited"
            :label="$t('message.unlimited_quantity')"></v-switch>
          <v-text-field class="pb-0 mb-0" :disabled="duplicateProduct.unlimited" outlined dense
            :label="$t('message.inventory_quantity') + ' (' + $t('message.optional') + ')'"
            v-model="duplicateProduct.availableQuantity"></v-text-field>
          <v-text-field outlined dense :label="$t('message.size')" v-model="duplicateProduct.size"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <PlusButton intent="primary" @click.stop="submitDuplicateProduct()">{{ $t('message.duplicate') }}</PlusButton>
          <PlusButton @click.stop="duplicateProductDialog = false">{{ $t('message.cancel') }}</PlusButton>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import ProductCategories from "@/components/inputs/ProductCategoriesCombo";
import AvailableClasses from "@/components/inputs/ScheduleClassesCombo.vue";
import PlusButton from "@/components/plus/PlusButton";
import PlusImageUploadCroppie from "@/components/plus/PlusImageUploadCroppie";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";
import { completeOnboardingStep } from "@/services/onboardingSteps";
import { mask } from "vue-the-mask";

export default {
  directives: {
    mask,
  },
  data() {
    return {
      loading: true,
      productId: this.$route.params.productId,
      formMessage: null,
      productType: null,
      automaticTaxes: false,
      product: null,
      showColorPicker: false,
      productImage: null,
      currency: null,
      currencyText: null,
      intervalText: null,
      availableClasses: [],
      restrictAttendance: false,
      duplicateProductDialog: false,
      duplicateProduct: /** @type {any} */ (null),
      price: null,
      mask1: "########",
      altStripeAccounts: null,
      stripeReady: false,
      paymentsReady: false,
      mask2: "##",
      priceDecimal: "00",
      updating: false,
      goods: [],
      subscriptions: [],
      categories: [],
      managedAcademy: this.$store.state.managedAcademy,
      deleteProductDialog: false,
      contracts: [],
      assignContract: false,
      deletingProduct: false,
      taxCodes: null,
      paymentsModule: null,
      inventory: [],
      loadingInventory: false,
      loadingMoreInventory: false,
      inventoryHasMore: false,
      inventoryPage: 0,
      showInventory: false,
      allowedClassCount: null,
      allowedClassCountInterval: null,
      allowedClassCountSendEmail: false,
      taxBehaviors: {
        exclusive: 'exclusive',
        inclusive: 'inclusive',
        none: null,
      },
      types: [
        { text: this.$t("message.goods"), value: "good" },
        { text: this.$t("message.manual_membership"), value: "good_membership" },
      ],
      intervals: [
      ],
      months: [
        { text: "1 " + this.$t("message.month"), value: 1 },
        { text: "2 " + this.$t("message.months"), value: 2 },
        { text: "3 " + this.$t("message.months"), value: 3 },
        { text: "4 " + this.$t("message.months"), value: 4 },
        { text: "5 " + this.$t("message.months"), value: 5 },
        { text: "6 " + this.$t("message.months"), value: 6 },
        { text: "7 " + this.$t("message.months"), value: 7 },
        { text: "8 " + this.$t("message.months"), value: 8 },
        { text: "9 " + this.$t("message.months"), value: 9 },
        { text: "10 " + this.$t("message.months"), value: 10 },
        { text: "11 " + this.$t("message.months"), value: 11 },
        { text: "12 " + this.$t("message.months"), value: 12 },
        { text: "13 " + this.$t("message.months"), value: 13 },
        { text: "14 " + this.$t("message.months"), value: 14 },
        { text: "15 " + this.$t("message.months"), value: 15 },
        { text: "16 " + this.$t("message.months"), value: 16 },
        { text: "17 " + this.$t("message.months"), value: 17 },
        { text: "18 " + this.$t("message.months"), value: 18 },
        { text: "19 " + this.$t("message.months"), value: 19 },
        { text: "20 " + this.$t("message.months"), value: 20 },
        { text: "21 " + this.$t("message.months"), value: 21 },
        { text: "22 " + this.$t("message.months"), value: 22 },
        { text: "23 " + this.$t("message.months"), value: 23 },
        { text: "24 " + this.$t("message.months"), value: 24 },
      ],
      allowedClassIntervals: [
        { text: this.$t("message.weekly"), value: "week" },
        { text: this.$t("message.monthly"), value: "month" },],
      onboardingStepComplete: false
    };
  },
  components: {
    PlusImageUploadCroppie,
    ProductCategories,
    PlusButton,
    AvailableClasses,
  },
  created() {
    this.loading = true;
    this.initPage();
    if (this.isAppAdmin())
      this.intervals.unshift({ text: this.$t("message.daily"), value: "day" });
    Api2()
      .post('trial/stepState', { "managedAcademy": this.$store.state.managedAcademy })
      .then((response) => {
        if (response?.data?.setupProductsMemberships) {
          this.onboardingStepComplete = true;
        }
      }).catch(() => {
        this.onboardingStepComplete = true;
      });
  },
  beforeRouteUpdate(to, from, next) {
    this.productId = to.params.productId;
    this.loading = true;
    this.product = null;
    this.productType = null;
    this.initPage();
    next();
  },
  watch: {
    productType: {
      handler: function (newValue) {
        if (!this.product) return;
        if (newValue == 'good_membership') {
          this.product.type = 'good';
          this.product.isMembership = true;
          this.product.category = this.$t("message.membership");
          this.product.manualRecurrence = true;
        } else {
          this.product.type = newValue;
          this.product.isMembership = false;
          this.product.category = '';
        }
        this.initTaxCode(this.product);
        console.log({ newValue });
      },
    },
    showColorPicker: {
      handler: function (newValue) {
        if (!newValue && this.product) this.product.color = null;
      },
    },
  },
  computed: {
    canDuplicate() {
      const p = /** @type {any} */ (this.product);
      return !!(p && p._id && p.type === 'good' && !p.isMembership);
    },
  },
  methods: {
    successUploadImage(uploadedFileData) {
      if (uploadedFileData && this.product) {
        this.product.image = uploadedFileData.location;
        this.productImage = uploadedFileData.original;
      }
    },
    initPage() {
      this.loading = true;
      if (this.$store.state.managedAcademy) {
        Api()
          .get(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/is_connected/all"
          )
          .then((response) => {
            this.stripeReady = response.data.stripeConnected;
            this.paymentsModule = response.data.paymentsModule;
            this.paymentsReady = response.data.paymentsReady;
            if (this.stripeReady || (response.data.paymentsReady && this.paymentsModule != 'stripe')) {
              this.types.push(
                { text: this.$t("message.recurrent_subscription"), value: "service" });
              if (this.paymentsModule == 'stripe') {
                this.intervals.push(
                  { text: this.$t("message.custom_day_cycle"), value: "custom-day-cycle" });
                this.intervals = [
                  { text: this.$t("message.weekly"), value: "week" },
                  { text: this.$t("message.bi_weekly"), value: "bi-week" },
                  { text: this.$t("message.monthly"), value: "month" },
                  { text: this.$t("message.bi_yearly"), value: "bi-year" },
                  { text: this.$t("message.yearly"), value: "year" },];
              } else if (this.paymentsModule == 'easypay') {
                this.intervals = [
                  { text: this.$t("message.weekly"), value: "week" },
                  { text: this.$t("message.monthly"), value: "month" }]
              }
            } else {
              this.types = [
                { text: this.$t("message.goods"), value: "good" },
                { text: this.$t("message.membership"), value: "good_membership" },
              ];
            }
          });
        Api()
          .get("admins/academy/" + this.$store.state.selectedAcademy)
          .then((response) => {
            this.currency = response.data.currency;
            this.currencyText = response.data.currency
              ? response.data.currency
              : "???";
          });
        if (this.productId == "new") {
          this.product = {
            taxBehavior: null,
            allowedCustomClasses: [],
            unlimited: false,
            allowedClassCount: {}
          };
          this.loading = false;
        } else {
          Api()
            .get(
              "admins/academy/" +
              this.$store.state.managedAcademy +
              "/products/" +
              this.productId
            )
            .then((response) => {
              this.product = response.data;
              if (!this.product.allowedClassCount || !this.product.allowedClassCount.interval) {
                this.product.allowedClassCount = {};
              } else {
                this.allowedClassCount = this.product.allowedClassCount.count;
                this.allowedClassCountInterval = this.product.allowedClassCount.interval;
                this.allowedClassCountSendEmail = this.product.allowedClassCount.sendEmail;
              }
              if (this.product.allowedClasses && this.product.allowedClasses.length)
                this.restrictAttendance = true;
              if (this.product.allowedCustomClasses && this.product.allowedCustomClasses.length)
                this.restrictAttendance = true;
              else
                this.product.allowedCustomClasses = [];
              if (this.product.allowedClassCount && this.product.allowedClassCount.interval)
                this.restrictAttendance = true;
              if (!this.product.taxBehavior)
                this.product.taxBehavior = null;
              if (this.product.amountDecimal > 0) {
                const amount = this.product.amount + "";
                this.price = amount.substring(0, amount.length - 2);
                this.priceDecimal = amount.substring(
                  amount.length - 2,
                  amount.length
                );
              } else {
                this.price = 0;
                this.priceDecimal = 0;
              }
              if (this.product.type != "service")
                this.getAltStripeAccounts();
              this.automaticTaxes = this.product.automaticTaxes;
              if (this.automaticTaxes)
                this.getTaxCodes();
              this.productImage = this.product.image;
              if (this.product.color) this.showColorPicker = true;
              this.loading = false;
              if (this.product.isMembership) {
                Api()
                  .get("documents/academy/" + this.$store.state.managedAcademy + "/contracts")
                  .then((response) => {
                    const data = response.data;
                    if (data) {
                      for (const c of data.contracts)
                        this.contracts.push({ value: c._id, text: c.name });
                      if (this.product.documentTemplate)
                        this.assignContract = true;
                    }
                  })
                  .catch((e) => {
                    if (e)
                      this.showAlert("error", this.$t("message.error_generic"));
                  });
                if (
                  this.product.amountStripe &&
                  this.product.amountStripe != this.product.amount
                ) {
                  this.showAlert(
                    "error",
                    this.$t("message.corrupted_item"),
                    this.$t("message.corrupted_item_explained"),
                    25000
                  );
                }
                for (const i of this.intervals) {
                  if (this.product.interval == i.value) {
                    this.intervalText = i.text;
                    break;
                  }
                }
              }
            })
            .catch((e) => {
              console.log("ERROR HERE", { e }, e);
              this.loading = false;
              if (e)
                this.showAlert("error", this.$t("message.error_generic"));
            });
        }
      } else {
        this.loading = false;
      }
    },
    initTaxCode(product) {
      product.taxCategory = 'txcd_99999999';
      if (product.type == 'service')
        product.taxCategory = 'txcd_20030000';
    },
    toggleInventory() {
      if (!this.showInventory) {
        this.showInventory = true;
        this.inventory = [];
        this.inventoryPage = 0;
        this.inventoryHasMore = false;
        this.getProductInventory(0);
      } else {
        this.showInventory = false;
      }
    },
    loadMoreInventory() {
      this.getProductInventory(this.inventoryPage + 1);
    },
    inventoryUserImage(i) {
      if (i.user && (i.user.userImageSmall || i.user.userImage))
        return i.user.userImageSmall || i.user.userImage;
      if (i.transaction && i.transaction.buyer &&
          (i.transaction.buyer.userImageSmall || i.transaction.buyer.userImage))
        return i.transaction.buyer.userImageSmall || i.transaction.buyer.userImage;
      return null;
    },
    goToTransaction(i) {
      if (i) {
        if (i.items && i.items.length > 0)
          this.navigateTo({ name: 'viewitemstransaction', params: { transactionId: i._id } });
        else
          this.navigateTo({ name: 'viewtransaction', params: { sessionId: i._id } });
      }
    },
    getProductInventory(page) {
      if (page === 0) {
        this.loadingInventory = true;
      } else {
        this.loadingMoreInventory = true;
      }
      Api()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/products/" + this.productId + "/inventory?page=" + (page || 0)
        )
        .then((response) => {
          const { data, hasMore, page: returnedPage } = response.data;
          if (returnedPage === 0) {
            this.inventory = data;
          } else {
            this.inventory = this.inventory.concat(data);
          }
          this.inventoryPage = returnedPage;
          this.inventoryHasMore = hasMore;
          this.loadingInventory = false;
          this.loadingMoreInventory = false;
        })
        .catch(() => {
          this.loadingInventory = false;
          this.loadingMoreInventory = false;
        });
    },
    addEditProduct() {
      if (this.paymentsModule == 'stripe' && !this.stripeReady && this.product.type == "service") {
        this.showAlert(
          "error",
          this.$t("message.need_connect_stripe_subscriptions"),
          8000
        );
        return;
      } else if (this.paymentsModule == 'easypay' && !this.paymentsReady) {
        this.showAlert(
          "error",
          this.$t("message.payments_not_ready"),
          8000
        );
        return;
      }
      if (!this.product.name || !this.product.type || this.price == null ||
        (this.product.type == 'service' && !this.product.interval)) {
        this.showAlert("error", this.$t("message.fill_all_params"));
        return;
      } else {
        this.updating = true;
        this.formMessage = null;
        if (this.priceDecimal == "") this.priceDecimal = "00";
        else if (this.priceDecimal.length == 1)
          this.priceDecimal = this.priceDecimal + "0";
        this.product.amount = new Number(this.price + this.priceDecimal);
        this.product.amountDecimal = new Number(
          this.price + "." + this.priceDecimal
        );
        if (this.product.amountDecimal == 0 && this.product.type == 'service') {
          this.showAlert("error", this.$t("message.recurrent_requires_amount"));
          return;
        }
        if (this.restrictAttendance && this.allowedClassCountInterval && !isNaN(this.allowedClassCount)) {
          this.product.allowedClassCount = {
            count: new Number(this.allowedClassCount).valueOf(),
            interval: this.allowedClassCountInterval, // month|week
            sendEmail: this.allowedClassCountSendEmail,
          };
        } else {
          this.product.allowedClassCount = null;
        }
        if (this.product._id) {
          if (!this.assignContract)
            this.product.documentTemplate = null;
          if (!this.restrictAttendance) {
            this.product.allowedClassCount = null;
            this.product.allowedClasses = [];
            this.product.allowedCustomClasses = [];
          }
          Api()
            .patch(
              "admins/academy/" +
              this.$store.state.managedAcademy +
              "/products",
              this.product
            )
            .then((response) => {
              const data = response.data;
              if (data) {
                this.showAlert("success", this.$t("message.updated"));
                this.getProductInventory();
                this.updating = false;
              }
            })
            .catch((e) => {
              this.updating = false;
              if (e)
                this.showAlert(
                  "error",
                  this.$t("message.error_generic")
                );
            });
        } else {
          this.updating = true;
          Api()
            .post(
              "admins/academy/" +
              this.$store.state.managedAcademy +
              "/products",
              this.product
            )
            .then((response) => {
              const data = response.data;
              if (data && data._id) {
                this.productId = data._id;
                this.updating = false;
                this.initPage();
                this.updateOnboardState();
              }
            })
            .catch((e) => {
              this.updating = false;
              if (e)
                this.showAlert(
                  "error",
                  this.$t("message.error_generic")
                );
            });
        }
      }
    },
    getTaxCodes() {
      Api()
        .get(
          "payments/academy/" + this.$store.state.managedAcademy + "/tax/codes"
        )
        .then((response) => {
          this.taxCodes = response.data.codes;
        });
    },
    getAltStripeAccounts() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/altstripeaccounts"
        )
        .then((response) => {
          if (response.data && response.data.accounts)
            this.altStripeAccounts = response.data.accounts;
        });
    },
    updateOnboardState() {
      if (!this.onboardingStepComplete) {
        completeOnboardingStep({
          step: "setupProductsMemberships",
          managedAcademy: this.$store.state.managedAcademy,
          store: this.$store
        }).then(({ stepState }) => {
          this.onboardingStepComplete = !!stepState?.setupProductsMemberships;
        });
      }
    },
    showDuplicateProductDialog() {
      const p = /** @type {any} */ (this.product);
      this.duplicateProduct = {
        price: this.price,
        priceDecimal: this.priceDecimal,
        name: p.name,
        size: p.size,
        availableQuantity: p.availableQuantity,
        unlimited: p.unlimited,
      };
      this.duplicateProductDialog = true;
    },
    submitDuplicateProduct() {
      const dp = /** @type {any} */ (this.duplicateProduct);
      if (dp.priceDecimal == "") dp.priceDecimal = "00";
      else if (dp.priceDecimal.length == 1)
        dp.priceDecimal = dp.priceDecimal + "0";
      dp.amount = new Number(dp.price + dp.priceDecimal);
      dp.amountDecimal = new Number(dp.price + "." + dp.priceDecimal);
      Api()
        .post(
          "admins/academy/" + this.$store.state.managedAcademy + "/products/" + (/** @type {any} */ (this.product))._id + "/duplicate",
          dp
        )
        .then((response) => {
          const data = response.data;
          if (data && data._id) {
            this.duplicateProductDialog = false;
            this.navigateTo({ name: 'manageproductplus', params: { productId: data._id }, crumbName: data.name || null });
          }
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    showDeleteProductDialog() {
      this.formMessage = null;
      this.deleteProductDialog = true;
    },
    deleteProduct() {
      this.deletingProduct = true;
      Api()
        .delete(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/" +
          this.productId
        )
        .then((response) => {
          if (response) this.navigateTo({ name: "productsmemberships" });
        })
        .catch((e) => {
          this.deletingProduct = false;
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
  },
};
</script>
